import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CartItem, CartProduct, CartVariant } from './types';
import { trackAddToCart } from '@/features/tracking/tracker';

// Cart state in zustand (replaces the prototype's CartContext). Persisted to
// localStorage so a refresh keeps the cart. Prices stored here are whatever the
// server quote returned; checkout re-validates them before payment.

interface CartState {
  items: CartItem[];
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  addProduct: (product: CartProduct, variant: CartVariant, quantity: number) => void;
  addService: (item: CartItem) => void;
  remove: (variantId: string) => void;
  setQuantity: (variantId: string, quantity: number) => void;
  clear: () => void;
}

export const useCartStore = create<CartState>()(
  persist(
    (set) => ({
      items: [],
      isOpen: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false }),
      toggle: () => set((s) => ({ isOpen: !s.isOpen })),

      addProduct: (product, variant, quantity) => {
        // Visitor analytics: record exactly which item this guest selected.
        trackAddToCart({
          id: variant.id,
          title: product.title,
          variant: variant.title,
          priceCents: variant.sale_price_in_cents ?? variant.price_in_cents,
          quantity,
        });
        set((s) => {
          const existing = s.items.find((i) => i.variant.id === variant.id);
          if (existing) {
            return {
              items: s.items.map((i) =>
                i.variant.id === variant.id ? { ...i, quantity: i.quantity + quantity } : i,
              ),
              isOpen: true,
            };
          }
          return { items: [...s.items, { product, variant, quantity }], isOpen: true };
        });
      },

      addService: (item) => {
        trackAddToCart({
          id: item.variant.id,
          title: item.product.title,
          variant: item.variant.title,
          priceCents: item.variant.sale_price_in_cents ?? item.variant.price_in_cents,
          quantity: item.quantity || 1,
        });
        set((s) => {
          const existing = s.items.find((i) => i.variant.id === item.variant.id);
          if (existing) {
            return {
              items: s.items.map((i) =>
                i.variant.id === item.variant.id
                  ? { ...i, quantity: i.quantity + (item.quantity || 1) }
                  : i,
              ),
              isOpen: true,
            };
          }
          return { items: [...s.items, { ...item, quantity: item.quantity || 1 }], isOpen: true };
        });
      },

      remove: (variantId) => set((s) => ({ items: s.items.filter((i) => i.variant.id !== variantId) })),

      setQuantity: (variantId, quantity) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.variant.id === variantId ? { ...i, quantity: Math.max(1, quantity) } : i,
          ),
        })),

      clear: () => set({ items: [] }),
    }),
    { name: 'firstseller-cart', partialize: (s) => ({ items: s.items }) },
  ),
);

/** Total in cents across the cart (sale price wins when present). */
export function cartTotalCents(items: CartItem[]): number {
  return items.reduce((total, item) => {
    const price = item.variant.sale_price_in_cents ?? item.variant.price_in_cents;
    return total + price * item.quantity;
  }, 0);
}

export function cartCount(items: CartItem[]): number {
  return items.reduce((n, i) => n + i.quantity, 0);
}
